import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  ArrowRight, 
  Search,
  Filter
} from "lucide-react";
import { products } from "../data/mock";

export const Products = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = ["All", ...new Set(products.map(p => p.category))];

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <Helmet>
        <title>Coating & Converting Machines - BOPP Tape, Slitter Rewinder | Hero Impex India</title>
        <meta name="description" content="Browse our range of coating machines, slitter rewinders, and converting equipment. BOPP tape coating, lamination, printing machines manufactured in India with German standards. Request quote today!" />
        <meta name="keywords" content="coating machines, slitter rewinder, BOPP tape machine, converting equipment, lamination machine, printing machine, packaging machinery, Hero Impex products" />
      </Helmet>
      
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-amber-500/20 text-amber-300 border-amber-500/30">Our Products</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Precision-Engineered Coating & Converting Machinery
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Discover our comprehensive range of BOPP tape machines, coating equipment, slitting machines, and converting machinery built to German quality standards.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-5 h-5 text-slate-600" />
              {categories.map(category => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700" : ""}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-2">No products found</h3>
              <p className="text-slate-600">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <Card key={product.id} className="group hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  <div className="relative overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-amber-600 text-white shadow-lg">{product.category}</Badge>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                      <Link to="/contact" className="w-full">
                        <Button className="w-full bg-white text-slate-900 hover:bg-slate-100">
                          Request Quote
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-xl mb-2 text-slate-900 group-hover:text-amber-600 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-slate-600 mb-4 leading-relaxed">
                      {product.description}
                    </p>

                    <div className="space-y-4">
                      {product.features && product.features.length > 0 && (
                        <div>
                          <div className="text-sm font-semibold text-slate-900 mb-2">Key Features:</div>
                          <ul className="space-y-1">
                            {product.features.slice(0, 3).map((feature, idx) => (
                              <li key={`${product.name}-feature-${idx}`} className="text-sm text-slate-600 flex items-start gap-2">
                                <span className="text-amber-600 mt-1">•</span>
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {product.specs && Object.keys(product.specs).length > 0 && (
                        <div className="border-t pt-4">
                          <div className="text-sm font-semibold text-slate-900 mb-2">Technical Specs:</div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {Object.entries(product.specs).slice(0, 4).map(([key, value]) => (
                              <div key={key}>
                                <div className="text-slate-500 text-xs capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                                <div className="text-slate-900 font-medium">{value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {product.specifications && Object.keys(product.specifications).length > 0 && (
                        <div className="border-t pt-4">
                          <div className="text-sm font-semibold text-slate-900 mb-2">Specifications:</div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {Object.entries(product.specifications).slice(0, 4).map(([key, value]) => (
                              <div key={key}>
                                <div className="text-slate-500 text-xs">{key}</div>
                                <div className="text-slate-900 font-medium">{value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="border-t pt-4 flex items-center justify-end">
                        <Link to="/contact">
                          <Button size="sm" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700">
                            Request Quote
                            <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                BOPP Tape Coating Machine Manufacturer in India
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Hero Impex is a leading BOPP tape coating machine manufacturer in Chennai, India. Our advanced BOPP tape making machines are designed for precision coating applications, transforming Biaxially Oriented Polypropylene (BOPP) film into high-quality adhesive tape. With over 45 years of experience, we deliver reliable, cost-effective solutions for tape manufacturers across India and internationally.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Our BOPP self-adhesive tape coating machines feature automatic tension control, adjustable coating thickness, and energy-efficient designs. Suitable for packaging tape, industrial tape, and specialty tape production, these machines are built to German engineering standards while maintaining competitive pricing.
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Slitting Rewinding Machine Supplier India
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                As a trusted slitting rewinding machine supplier, Hero Impex offers high-precision machines for converting jumbo rolls into customer-specific widths. Our slitting machines are perfect for film, paper, and foil processing industries, featuring PLC-based automation, pneumatic shaft loading, and precise edge alignment systems.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Whether you need a BOPP tape slitting machine, film slitting machine, or general-purpose slitter rewinder, our range covers speeds up to 200 m/min with working widths from 1000mm to 1500mm. Ideal for flexible packaging, label manufacturing, and lamination industries.
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Coating Machine Manufacturer Chennai
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Hero Impex specializes in manufacturing coating machines for various applications including paper coating, adhesive coating, and lamination. Our air knife coating machines and doctor blade coating systems deliver uniform coating distribution with excellent surface finish.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Located in Chennai, Tamil Nadu, we serve clients across India and export to 20+ countries. Our coating machines support both water-based and solvent-based coatings, with advanced drying systems and quality control sensors for consistent output quality.
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Custom Machinery Solutions
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Understanding that every manufacturing operation has unique requirements, we offer customized machinery solutions. From modified coating widths to specialized slitting configurations, our engineering team works closely with clients to deliver machines that perfectly match their production needs and budget constraints.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Help Choosing the Right Machine?
          </h2>
          <p className="text-lg text-amber-100 mb-8 max-w-2xl mx-auto">
            Our technical experts are ready to help you select the perfect machinery for your production requirements.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-amber-600 hover:bg-slate-100 shadow-xl">
              Contact Our Experts
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};
